import React, { useEffect, useRef, useState } from "react";
import { X, Video, VideoOff, Mic, MicOff, Loader2, Crown, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMediaStream } from "@/hooks/useMediaStream";
import { cn } from "@/lib/utils";

interface ExhibitionGoLiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoLive: (stream: MediaStream) => void;
  boothName: string;
  cityName?: string;
  countryFlag?: string;
  isHost?: boolean;
}

const ExhibitionGoLiveModal: React.FC<ExhibitionGoLiveModalProps> = ({
  isOpen,
  onClose,
  onGoLive,
  boothName,
  cityName,
  countryFlag,
  isHost = false,
}) => {
  const {
    stream,
    videoEnabled,
    audioEnabled,
    isLoading,
    error,
    startStream,
    stopStream,
    toggleVideo,
    toggleAudio,
  } = useMediaStream();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [streamValidated, setStreamValidated] = useState(false);
  const [streamError, setStreamError] = useState<string | null>(null);

  // Request stream when modal opens
  useEffect(() => {
    if (isOpen && !stream && !isLoading) {
      setStreamError(null);
      setStreamValidated(false);
      startStream(true, true);
    }
    return () => {
      if (!isOpen && stream) {
        stopStream();
      }
    };
  }, [isOpen]);

  // Attach stream to video element and validate it's working
  useEffect(() => {
    if (videoRef.current && stream) {
      const videoEl = videoRef.current;
      videoEl.srcObject = stream;

      const handleCanPlay = () => {
        console.log("[GoLive] Video can play - stream validated");
        videoEl.play().catch((err) => {
          console.error("[GoLive] Video play failed:", err);
          setStreamError("Failed to start video playback");
        });
        setIsReady(true);
        
        // Validate video track is actually producing frames
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState === "live" && videoTrack.enabled) {
          setStreamValidated(true);
          setStreamError(null);
        } else {
          setStreamError("Video track not active");
          setStreamValidated(false);
        }
      };

      const handleError = (e: Event) => {
        console.error("[GoLive] Video error:", e);
        setStreamError("Video stream error");
        setStreamValidated(false);
      };

      videoEl.addEventListener("canplay", handleCanPlay);
      videoEl.addEventListener("error", handleError);

      return () => {
        videoEl.removeEventListener("canplay", handleCanPlay);
        videoEl.removeEventListener("error", handleError);
      };
    }
  }, [stream]);

  // Handle close
  const handleClose = () => {
    stopStream();
    setIsReady(false);
    setStreamValidated(false);
    setStreamError(null);
    setCountdown(null);
    onClose();
  };

  // Retry stream request
  const handleRetry = () => {
    setStreamError(null);
    setStreamValidated(false);
    stopStream();
    setTimeout(() => {
      startStream(true, true);
    }, 100);
  };

  // Handle Go Live with countdown - ONLY if stream is validated
  const handleGoLive = () => {
    if (!stream || !videoEnabled || !streamValidated) {
      console.warn("[GoLive] Cannot go live - stream not validated");
      return;
    }

    // Final validation check
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack || videoTrack.readyState !== "live" || !videoTrack.enabled) {
      setStreamError("Video stream is not active. Please try again.");
      return;
    }

    console.log("[GoLive] Stream validated, starting countdown");
    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(interval);
          onGoLive(stream);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Determine if Go Live button should be enabled
  const canGoLive = isReady && videoEnabled && streamValidated && !streamError && countdown === null && !isLoading;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md bg-gray-900 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{countryFlag || "💖"}</span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-white font-semibold">
                  {isHost ? "Open Your Booth" : "Go Live"}
                </h2>
                {isHost && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                    <Crown className="w-3 h-3" />
                    Host
                  </span>
                )}
              </div>
              <p className="text-white/60 text-sm">{boothName}{cityName && ` • ${cityName}`}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Camera Preview */}
        <div className="relative aspect-[4/3] bg-black">
          {/* Video Element */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={cn(
              "w-full h-full object-cover transform scale-x-[-1]",
              !videoEnabled && "hidden"
            )}
          />

          {/* Loading State */}
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900">
              <Loader2 className="w-10 h-10 text-white animate-spin mb-3" />
              <p className="text-white/70 text-sm">Requesting camera access...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 p-6 text-center">
              <VideoOff className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-white font-medium mb-2">Camera Access Required</p>
              <p className="text-white/60 text-sm mb-4">{error}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="border-white/30 text-white hover:bg-white/10 gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
            </div>
          )}

          {/* Stream Validation Error */}
          {streamError && !error && (
            <div className="absolute bottom-16 left-0 right-0 px-4">
              <div className="bg-red-500/90 text-white text-xs py-2 px-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{streamError}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRetry}
                  className="ml-auto h-6 px-2 text-white hover:bg-white/20"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Video Off State */}
          {stream && !videoEnabled && !isLoading && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-800">
              <VideoOff className="w-16 h-16 text-white/40 mb-3" />
              <p className="text-white/60 text-sm">Camera is off</p>
            </div>
          )}

          {/* Countdown Overlay */}
          {countdown !== null && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70">
              <div className="text-center">
                <div className="text-7xl font-bold text-white animate-pulse mb-2">
                  {countdown}
                </div>
                <p className="text-white/70">Going live...</p>
              </div>
            </div>
          )}

          {/* Controls Overlay */}
          {stream && !countdown && (
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-4">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleAudio}
                className={cn(
                  "rounded-full w-12 h-12 border-2",
                  audioEnabled
                    ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
                    : "border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                )}
              >
                {audioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleVideo}
                className={cn(
                  "rounded-full w-12 h-12 border-2",
                  videoEnabled
                    ? "border-white/30 bg-white/10 text-white hover:bg-white/20"
                    : "border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30"
                )}
              >
                {videoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 space-y-3">
          {/* Info Note */}
          <p className="text-white/50 text-xs text-center">
            {isHost
              ? videoEnabled && streamValidated
                ? "Your booth will become visible when you go live"
                : "Turn on your camera to open your booth"
              : videoEnabled && streamValidated
              ? "You'll be visible to others in this booth"
              : "Turn on your camera to join live"}
          </p>

          {/* Go Live Button */}
          <Button
            onClick={handleGoLive}
            disabled={!canGoLive}
            className={cn(
              "w-full font-semibold py-6 text-lg",
              isHost
                ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                : "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            )}
          >
            {countdown !== null ? (
              "Starting..."
            ) : isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Connecting...
              </>
            ) : !streamValidated && stream ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Validating stream...
              </>
            ) : (
              <>
                <Video className="w-5 h-5 mr-2" />
                {isHost ? "Open Booth" : "Go Live"}
              </>
            )}
          </Button>

          {/* Cancel */}
          <Button
            variant="ghost"
            onClick={handleClose}
            className="w-full text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionGoLiveModal;
